import { Button as MuiButton } from '@mui/material';
import type { ButtonProps as MuiButtonProps } from '@mui/material';
import { tss } from 'tss-react/mui';
import { Link as RouterLink } from 'react-router';
import type { To } from 'react-router';
import { forwardRef } from 'react';

const useStyles = tss.create(({ theme }) => ({
    root: {
        borderRadius: 4,
        boxShadow: 'none',
        fontWeight: 'bold',
        fontSize: 12,
        lineHeight: '16px',
        height: 42,
        padding: '12px 12px 10px 12px',
        transitionProperty:
            'background-color, border-color, opacity, color, box-shadow',
    },
    containedPrimary: {
        color: theme.bino.color.white,
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        '&:hover': {
            color: theme.bino.color.white,
            backgroundColor: theme.bino.color.darkPurple,
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: theme.palette.primary.main,
            },
        },
        '&:disabled': {
            color: theme.bino.color.white,
            borderColor: 'transparent',
            backgroundColor: theme.bino.color.backgroundLightPurpleRGBA,
        },
    },
    containedSecondary: {
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        backgroundColor: theme.bino.color.white,
        '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.bino.color.white,
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: theme.bino.color.white,
            },
        },
        '&:disabled': {
            opacity: 0.5,
            color: theme.palette.primary.main,
            backgroundColor: theme.bino.color.white,
            border: `1px solid ${theme.palette.primary.main}`,
        },
    },
    nowrap: {
        whiteSpace: 'nowrap',
    },
}));

export interface ButtonProps extends MuiButtonProps {
    wrap?: boolean;
    // FIXME It seems like the component prop doesnt work as expected with typescript.
    component?: any;
    to?: To;
    target?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            color = 'primary' as MuiButtonProps['color'],
            variant = 'contained' as MuiButtonProps['variant'],
            wrap = false,
            component,
            ...props
        },
        ref,
    ) => {
        const { classes, cx } = useStyles();
        if (props.to && component && component !== RouterLink) {
            console.error('Component must be RouterLink if props.to is set');
        }
        return (
            <MuiButton
                ref={ref}
                {...props}
                component={props.to ? RouterLink : component}
                color={color}
                variant={variant}
                classes={{
                    root: cx({
                        [classes.root]: true,
                        [classes.nowrap]: wrap === false,
                    }),
                    containedPrimary: classes.containedPrimary,
                    containedSecondary: classes.containedSecondary,
                }}
            />
        );
    },
);
export default Button;
